import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_LOCATIONS } from "../../../utils/constants";
import { LocationInterface } from "../../../models/TicketModule/Location";
import locationService from "../../../services/ticketService/locationService";

interface UpdateLocationsContext {
  previousLocations: LocationInterface[];
}

const useUpdateLocation = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    LocationInterface,
    Error,
    LocationInterface,
    UpdateLocationsContext
  >({
    mutationFn: async (location: LocationInterface) => {
      // Assuming the ID is stored in the location object or can be derived
      const { id, ...locationData } = location;
      return locationService.Update(id || "", locationData);
    },

    onMutate: async (updatedLocation: LocationInterface) => {
      await queryClient.cancelQueries({
        queryKey: [
          CACHE_KEY_LOCATIONS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
      });

      const previousLocations =
        queryClient.getQueryData<LocationInterface[]>([
          CACHE_KEY_LOCATIONS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      queryClient.setQueryData<LocationInterface[]>(
        [
          CACHE_KEY_LOCATIONS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (locations = []) =>
          locations.map((location) =>
            location.id === updatedLocation.id ? updatedLocation : location
          )
      );

      return { previousLocations };
    },

    onSuccess: (savedLocation) => {
      queryClient.setQueryData<LocationInterface[]>(
        [
          CACHE_KEY_LOCATIONS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (locations) =>
          locations?.map((location) =>
            location.id === savedLocation.id ? savedLocation : location
          )
      );
    },

    onError: (error, updatedLocation, context) => {
      if (!context) return;

      queryClient.setQueryData<LocationInterface[]>(
        [
          CACHE_KEY_LOCATIONS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousLocations
      );
    },
  });
};

export default useUpdateLocation;
